import React, { PropsWithChildren, ReactElement } from 'react';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Sidebar } from 'primereact/sidebar';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SvgTrashBin from '@icons/delete-trash.svg?react';
import clsx from 'clsx';
import CloseButton from '@components/RingilComponents/CloseButton.tsx';
import useScrollBehindPanel from '@hooks/useScrollBehindPanel/useScrollBehindPanel.tsx';
import { sidebarTransitionOptions } from '@components/ringil3/Sidebar/sidebarTransition.ts';
import usePanelDock from '@components/panelDock/usePanelDock.ts';
import PanelDockControls from '@components/panelDock/PanelDockControls.tsx';
import MinimizedPanelPill from '@components/panelDock/MinimizedPanelPill.tsx';

/** The three panel widths from the design: 550 / 880 / 1100. */
export type PanelSize = 'sm' | 'md' | 'lg';

export const panelSizeClass = (size: PanelSize) => clsx({ 'panel-md': size === 'md', 'panel-lg': size === 'lg' });

interface ComponentProps {
  visible: boolean;
  heading: string | JSX.Element;
  onContinue: () => void;
  onBack: () => void;
  loading?: boolean;
  continueLabel?: string | JSX.Element;
  continueIcon?: ReactElement;
  dataTestIdContinue?: string;
  dataTestIdBack?: string;
  showSaveButton?: boolean;
  showBackButton?: boolean;
  disableSaveButton?: boolean;
  /** 'sm' 550px, 'md' 880px, 'lg' 1100px. */
  size?: PanelSize;
  /**
   * @deprecated Use `size`. Kept so callers with a bespoke width keep working during the migration;
   * it is applied as the panel width variable so placement still behaves.
   */
  maxWidth?: string;
  /** Extra footer content for panels with more actions than continue/back. */
  footerExtra?: ReactElement;
  onDeleteForm?: () => void;
  /**
   * Opt in to docking (components/panelDock): the user can move the panel to the left edge and minimize
   * it to a pill while they look something up on the page. The value is the key its side is remembered
   * under — unique per kind of panel, e.g. 'create-reservation'.
   *
   * The panel stays modal while expanded and never floats: a form wants the user's attention, and
   * leaving the page live behind it would let two forms be edited at once. Leave unset (the default) and
   * nothing about the panel changes.
   */
  dockKey?: string;
}

const RingilSidebar: React.FC<PropsWithChildren<ComponentProps>> = ({
  visible,
  heading,
  onContinue,
  onBack,
  children,
  loading = false,
  continueLabel,
  continueIcon = null,
  dataTestIdContinue,
  dataTestIdBack,
  showSaveButton = true,
  showBackButton = true,
  disableSaveButton = false,
  size = 'sm',
  maxWidth,
  footerExtra,
  onDeleteForm,
  dockKey,
}) => {
  const { tr } = useTranslationLgs();
  useScrollBehindPanel(visible);
  // Always called (hooks cannot be conditional); without a dockKey it hands back the plain props.
  const dock = usePanelDock(dockKey ?? 'ringil-sidebar', { visible, onHide: onBack, disabled: !dockKey });
  return (
    <>
      {/*@ts-ignore*/}
      <Sidebar
        transitionOptions={sidebarTransitionOptions}
        // visible / onHide / modal / position — the given ones, adjusted while docked elsewhere or minimized.
        {...dock.sidebarProps}
        // Width comes from the panel-size class; a legacy maxWidth overrides the variable so those
        // callers keep their width and still get the new placement.
        style={{ backgroundColor: 'var(--ringil3-base-color)', ...(maxWidth ? { ['--ringil-panel-width' as any]: maxWidth } : {}) }}
        className={clsx('sidebar-modal', panelSizeClass(size), dock.className)}
        // PrimeReact's own close button is a grey pi-times circle; ours is the same cross the
        // dialogs use, rendered in the header's icons slot instead.
        showCloseIcon={false}
        icons={() => (
          <>
            <SidebarHeader>{heading}</SidebarHeader>
            <div className="flex items-center gap-2">
              <PanelDockControls dock={dock} />
              {/* The sidebar header pads 1rem, not the dialog's 24px, so the cross is not pulled out. */}
              <CloseButton onClick={onBack} className={'mr-0'} />
            </div>
          </>
        )}
        closeOnEscape={true}
        dismissable={false}
        // The mask still blocks clicks; useScrollBehindPanel forwards the wheel to the content.
        // (Declared after the spread: a RingilSidebar never locks the page's scroll.)
        blockScroll={false}
      >
        {children}
        <div className="flex items-center justify-between flex-nowrap gap-2 mt-4 w-full">
          <div className="flex items-center gap-2">
            {showSaveButton === true && (
              <RingilButton
                label={continueLabel ? continueLabel : tr(`RingilSidebar.continue`, `Continue`)}
                onClick={() => onContinue()}
                mode={'filled'}
                size={'big'}
                loading={loading}
                icon={continueIcon}
                disabled={disableSaveButton}
                dataTestId={dataTestIdContinue}
              />
            )}

            {showBackButton && <RingilButton label={tr(`RingilSidebar.back`, `Back`)} onClick={() => onBack()} mode={'link'} size={'big'} dataTestId={dataTestIdBack} />}
          </div>

          <div className="flex items-center gap-2">
            {footerExtra}
            {onDeleteForm && (
              <RingilButton
                label={tr(`RingilSidebar.deleteForm`, `Delete form`)}
                icon={<SvgTrashBin className="w-4.5" />}
                onClick={() => onDeleteForm()}
                mode={'outlined-rectangle'}
                size={'big'}
              />
            )}
          </div>
        </div>
      </Sidebar>
      {dockKey && <MinimizedPanelPill dock={dock} label={typeof heading === 'string' ? heading : tr('RingilSidebar.minimizedPanel', 'Rozpracovaný formulář')} />}
    </>
  );
};

const SidebarHeader = styled.div`
  display: flex;
  flex: 1;
  font-family: 'DM Sans';
  font-weight: 400;
  font-size: 24px;
  padding: 0.5rem 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export default RingilSidebar;
